import React, { useCallback, useEffect, useState } from 'react';
import { formatDistance } from 'date-fns';
import Page from '../../../components/Page';
import Breadcrumb from '../../../components/Breadcrumb';
import client from '../../../lib/feathers';

// Time-window options for the filter strip. `daysBack: 0` is the unbounded
// "all time" option — matches the value the API treats as no date filter.
// Default selection is 24h: this report is signal, and old searches no
// longer reflect what's missing today.
const RANGES = [
  { key: '24h', label: '24h', daysBack: 1 },
  { key: '7d', label: '7d', daysBack: 7 },
  { key: '30d', label: '30d', daysBack: 30 },
  { key: '90d', label: '90d', daysBack: 90 },
  { key: 'year', label: 'Year', daysBack: 365 },
  { key: 'all', label: 'All', daysBack: 0 },
];

export default function NoResultsSearch() {
  const [results, setResults] = useState(null);
  const [rangeKey, setRangeKey] = useState('24h');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const range = RANGES.find((r) => r.key === rangeKey) || RANGES[0];
    try {
      const service = client.service('/admin/report-no-result-search');
      const data = await service.find({ query: { $daysBack: range.daysBack } });
      setResults(data);
    } catch (err) {
      setError(err.message || 'Failed to load report');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [rangeKey]);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <Page title="Reports">
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[{ name: 'Reports', href: '/admin/reports' }, { name: 'No Result Search' }]} />
        </nav>
      </div>

      <div className="py-5">
        <div className="flex flex-col w-full">
          <div className="flex items-baseline justify-between mb-4">
            <h1 className="text-2xl">Searches with No Results</h1>
            <div className="text-sm text-gray-500">
              {loading ? 'Loading…' : `${results?.length ?? 0} result${results?.length === 1 ? '' : 's'}`}
            </div>
          </div>

          {/* Time-window filter. Buttons rather than a select so the
              current view is always visible at a glance. */}
          <div className="flex flex-wrap gap-2 mb-5">
            {RANGES.map((r) => {
              const active = r.key === rangeKey;
              return (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setRangeKey(r.key)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                    active
                      ? 'bg-icon text-white border-icon'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {r.label}
                </button>
              );
            })}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 mb-4">
              {error}
            </div>
          )}

          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Keywords
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        When
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {results && results.map((u) => (
                      <tr className="hover:bg-gray-100" key={u.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-green-800">
                            {u.keywords}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {u.name || <span className="text-gray-400">anonymous</span>}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900" title={u.createdAt}>
                            {formatDistance(u.createdAt, new Date(), { addSuffix: true })}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!loading && results && results.length === 0 && (
                      <tr>
                        <td colSpan="3" className="px-6 py-12 text-center text-sm text-gray-500">
                          No no-result searches in this window.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
}
